// src/services/optimisticService.js
// Optimistic update service for instant UI updates with automatic rollback

import cacheService from './cacheService';

/**
 * Pending operations tracker
 * Stores rollback data for each in-flight operation
 */
const pendingOperations = new Map();

/**
 * Generate a unique operation ID
 */
function generateOperationId() {
  return `op_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Execute an optimistic update
 * Updates UI immediately, then syncs with server
 *
 * @param {Object} config - Configuration object
 * @returns {Promise} Result of the operation
 */
export async function optimisticUpdate(config) {
  const {
    // Cache configuration
    cacheType,
    cacheParams = null,

    // Optimistic update function (receives current data, returns new data)
    optimisticFn,

    // Server mutation function
    mutationFn,

    // Optional callbacks
    onOptimisticUpdate = null,
    onSuccess = null,
    onError = null,
    onRollback = null,

    // Retry configuration
    retryOnFail = false,
    maxRetries = 2,
  } = config;

  const operationId = generateOperationId();

  // Get current cached data for potential rollback
  const currentCached = cacheService.get(cacheType, cacheParams);
  const rollbackData = currentCached?.data ?? null;

  // Apply optimistic update
  const optimisticData = cacheService.optimisticUpdate(
    cacheType,
    cacheParams,
    optimisticFn
  );

  // Store rollback information
  pendingOperations.set(operationId, {
    cacheType,
    cacheParams,
    rollbackData,
    optimisticData,
    timestamp: Date.now(),
  });

  // Notify of optimistic update
  if (onOptimisticUpdate) {
    onOptimisticUpdate(optimisticData);
  }

  try {
    // Execute server mutation
    let result;
    let attempts = 0;

    while (attempts <= (retryOnFail ? maxRetries : 0)) {
      try {
        result = await mutationFn();
        break;
      } catch (error) {
        attempts++;
        if (attempts > maxRetries || !retryOnFail) {
          throw error;
        }
        // Wait before retry (exponential backoff)
        await new Promise(r => setTimeout(r, Math.pow(2, attempts) * 500));
      }
    }

    // Success - update cache with server response if provided
    if (result?.data) {
      cacheService.set(cacheType, result.data, cacheParams);
    }

    // Clean up pending operation
    pendingOperations.delete(operationId);

    if (onSuccess) {
      onSuccess(result);
    }

    return { success: true, data: result?.data ?? optimisticData, operationId };

  } catch (error) {
    // Rollback on failure
    const operation = pendingOperations.get(operationId);

    if (operation && operation.rollbackData !== null) {
      cacheService.set(cacheType, operation.rollbackData, cacheParams);

      if (onRollback) {
        onRollback(operation.rollbackData, error);
      }
    } else {
      // No rollback data - invalidate cache
      cacheService.invalidate(cacheType, cacheParams);
    }

    pendingOperations.delete(operationId);

    if (onError) {
      onError(error);
    }

    return { success: false, error: error.message, operationId, rolledBack: true };
  }
}

/**
 * Optimistic CREATE operation
 * Adds a new item to a list optimistically
 */
export async function optimisticCreate(config) {
  const {
    cacheType,
    cacheParams = null,
    newItem,
    mutationFn,
    idField = 'id',
    tempIdPrefix = 'temp_',
    insertPosition = 'start', // 'start' or 'end'
    ...rest
  } = config;

  // Generate temporary ID for new item
  const tempId = `${tempIdPrefix}${Date.now()}`;
  const optimisticItem = {
    ...newItem,
    [idField]: tempId,
    _isOptimistic: true,
    _createdAt: Date.now(),
  };

  return optimisticUpdate({
    cacheType,
    cacheParams,
    optimisticFn: (currentData) => {
      const list = Array.isArray(currentData) ? currentData : [];
      return insertPosition === 'start'
        ? [optimisticItem, ...list]
        : [...list, optimisticItem];
    },
    mutationFn: async () => {
      const result = await mutationFn();
      // Return data with real ID for cache update
      return result;
    },
    onSuccess: (result) => {
      // Replace temp item with real item from server
      if (result?.data) {
        const realItem = result.data;
        cacheService.optimisticUpdate(cacheType, cacheParams, (data) => {
          if (!Array.isArray(data)) return [realItem];
          return data.map(item =>
            item[idField] === tempId ? { ...realItem, _isOptimistic: false } : item
          );
        });
      }
      if (rest.onSuccess) rest.onSuccess(result);
    },
    ...rest,
  });
}

/**
 * Optimistic UPDATE operation
 * Updates an existing item in a list optimistically
 */
export async function optimisticUpdateItem(config) {
  const {
    cacheType,
    cacheParams = null,
    itemId,
    updates,
    mutationFn,
    idField = 'id',
    ...rest
  } = config;

  return optimisticUpdate({
    cacheType,
    cacheParams,
    optimisticFn: (currentData) => {
      if (!Array.isArray(currentData)) return currentData;
      return currentData.map(item =>
        item[idField] === itemId
          ? { ...item, ...updates, _isOptimistic: true, _updatedAt: Date.now() }
          : item
      );
    },
    mutationFn,
    onSuccess: (result) => {
      // Update with server response
      if (result?.data) {
        cacheService.optimisticUpdate(cacheType, cacheParams, (data) => {
          if (!Array.isArray(data)) return data;
          return data.map(item =>
            item[idField] === itemId
              ? { ...result.data, _isOptimistic: false }
              : item
          );
        });
      }
      if (rest.onSuccess) rest.onSuccess(result);
    },
    ...rest,
  });
}

/**
 * Optimistic DELETE operation
 * Removes an item from a list optimistically
 */
export async function optimisticDelete(config) {
  const {
    cacheType,
    cacheParams = null,
    itemId,
    mutationFn,
    idField = 'id',
    softDelete = false, // If true, marks as deleted instead of removing
    ...rest
  } = config;

  return optimisticUpdate({
    cacheType,
    cacheParams,
    optimisticFn: (currentData) => {
      if (!Array.isArray(currentData)) return currentData;

      if (softDelete) {
        return currentData.map(item =>
          item[idField] === itemId
            ? { ...item, _deleted: true, _isOptimistic: true }
            : item
        );
      }

      return currentData.filter(item => item[idField] !== itemId);
    },
    mutationFn,
    ...rest,
  });
}

/**
 * Optimistic TOGGLE operation
 * Toggle a boolean field on an item
 */
export async function optimisticToggle(config) {
  const {
    cacheType,
    cacheParams = null,
    itemId,
    field,
    mutationFn,
    idField = 'id',
    ...rest
  } = config;

  return optimisticUpdate({
    cacheType,
    cacheParams,
    optimisticFn: (currentData) => {
      if (!Array.isArray(currentData)) return currentData;
      return currentData.map(item =>
        item[idField] === itemId
          ? { ...item, [field]: !item[field], _isOptimistic: true }
          : item
      );
    },
    mutationFn,
    ...rest,
  });
}

/**
 * Optimistic INCREMENT/DECREMENT operation
 * Increment or decrement a numeric field
 */
export async function optimisticIncrement(config) {
  const {
    cacheType,
    cacheParams = null,
    itemId,
    field,
    amount = 1,
    mutationFn,
    idField = 'id',
    min = null, // Minimum value constraint
    max = null, // Maximum value constraint
    ...rest
  } = config;

  return optimisticUpdate({
    cacheType,
    cacheParams,
    optimisticFn: (currentData) => {
      if (!Array.isArray(currentData)) return currentData;
      return currentData.map(item => {
        if (item[idField] !== itemId) return item;

        let newValue = (item[field] || 0) + amount;
        if (min !== null) newValue = Math.max(min, newValue);
        if (max !== null) newValue = Math.min(max, newValue);

        return { ...item, [field]: newValue, _isOptimistic: true };
      });
    },
    mutationFn,
    ...rest,
  });
}

/**
 * Optimistic REORDER operation
 * Move an item in a list (for drag-and-drop)
 */
export async function optimisticReorder(config) {
  const {
    cacheType,
    cacheParams = null,
    fromIndex,
    toIndex,
    mutationFn,
    ...rest
  } = config;

  return optimisticUpdate({
    cacheType,
    cacheParams,
    optimisticFn: (currentData) => {
      if (!Array.isArray(currentData)) return currentData;

      const newList = [...currentData];
      const [movedItem] = newList.splice(fromIndex, 1);
      newList.splice(toIndex, 0, { ...movedItem, _isOptimistic: true });

      // Update order/position fields if they exist
      return newList.map((item, index) => ({
        ...item,
        order: index,
        position: index,
      }));
    },
    mutationFn,
    ...rest,
  });
}

/**
 * Optimistic STATUS change
 * Common pattern for changing status (e.g., quote status, job status)
 */
export async function optimisticStatusChange(config) {
  const {
    cacheType,
    cacheParams = null,
    itemId,
    newStatus,
    statusField = 'status',
    mutationFn,
    idField = 'id',
    additionalUpdates = {},
    ...rest
  } = config;

  return optimisticUpdate({
    cacheType,
    cacheParams,
    optimisticFn: (currentData) => {
      if (!Array.isArray(currentData)) return currentData;
      return currentData.map(item =>
        item[idField] === itemId
          ? {
              ...item,
              [statusField]: newStatus,
              ...additionalUpdates,
              _isOptimistic: true,
              _statusChangedAt: Date.now(),
            }
          : item
      );
    },
    mutationFn,
    ...rest,
  });
}

/**
 * Batch optimistic updates
 * Perform multiple optimistic updates atomically
 */
export async function optimisticBatch(operations) {
  const operationIds = [];
  const rollbacks = [];

  // Apply all optimistic updates first
  for (const op of operations) {
    const { cacheType, cacheParams, optimisticFn } = op;
    const currentCached = cacheService.get(cacheType, cacheParams);

    rollbacks.push({
      cacheType,
      cacheParams,
      data: currentCached?.data ?? null,
    });

    cacheService.optimisticUpdate(cacheType, cacheParams, optimisticFn);
    operationIds.push(generateOperationId());
  }

  try {
    // Execute all mutations in parallel
    const results = await Promise.all(
      operations.map(op => op.mutationFn())
    );

    return {
      success: true,
      results,
      operationIds,
    };

  } catch (error) {
    // Rollback all operations
    for (const rollback of rollbacks) {
      if (rollback.data !== null) {
        cacheService.set(rollback.cacheType, rollback.data, rollback.cacheParams);
      } else {
        cacheService.invalidate(rollback.cacheType, rollback.cacheParams);
      }
    }

    return {
      success: false,
      error: error.message,
      rolledBack: true,
    };
  }
}

/**
 * Get all pending operations (useful for debugging)
 */
export function getPendingOperations() {
  return Array.from(pendingOperations.entries()).map(([id, op]) => ({
    id,
    ...op,
  }));
}

/**
 * Cancel a pending operation and rollback
 */
export function cancelOperation(operationId) {
  const operation = pendingOperations.get(operationId);

  if (operation) {
    if (operation.rollbackData !== null) {
      cacheService.set(
        operation.cacheType,
        operation.rollbackData,
        operation.cacheParams
      );
    }
    pendingOperations.delete(operationId);
    return true;
  }

  return false;
}

/**
 * Clear all pending operations (useful for logout/cleanup)
 */
export function clearPendingOperations() {
  pendingOperations.clear();
}

// Export all functions
const optimisticService = {
  optimisticUpdate,
  optimisticCreate,
  optimisticUpdateItem,
  optimisticDelete,
  optimisticToggle,
  optimisticIncrement,
  optimisticReorder,
  optimisticStatusChange,
  optimisticBatch,
  getPendingOperations,
  cancelOperation,
  clearPendingOperations,
};

export default optimisticService;
